import { createSelector } from '@reduxjs/toolkit';
import { reduce } from 'ramda';

import ooeConstants from '../constants';
import { selectDestination } from '../order/selectors';
import { selectMenu } from './reducer';
import { MenuItem } from './types';
import { getCombinedMenu, indexedMenuItems } from './utils';

export const selectDeliveryMenu = createSelector(
  selectMenu,
  (menu) => menu.delivery ?? { categories: [], itemGroups: [] },
);

export const selectPickupMenu = createSelector(
  selectMenu,
  (menu) => menu.pickup ?? { categories: [], itemGroups: [] },
);

export const selectCombinedDeliveryMenu = createSelector(selectDeliveryMenu, (menu) => getCombinedMenu(menu));

export const selectCombinedPickupMenu = createSelector(selectPickupMenu, (menu) => getCombinedMenu(menu));

export const selectCombinedMenu = createSelector(
  selectCombinedDeliveryMenu,
  selectCombinedPickupMenu,
  selectDestination,
  (deliveryMenu, pickupMenu, destination) => {
    return destination === ooeConstants.DELIVERY ? deliveryMenu : pickupMenu;
  },
);

export const selectCombinedMenuWithoutPaperGoods = createSelector(selectCombinedMenu, (menu) =>
  menu.filter((category) => category.tag !== ooeConstants.PAPER_GOODS_MENU_TAG),
);

export const selectMenuPaperGoods = createSelector(selectCombinedMenu, (menu) =>
  menu.find((category) => category.tag === ooeConstants.PAPER_GOODS_MENU_TAG),
);

export const hasItemizedMenuPaperGoods = createSelector(
  selectMenuPaperGoods,
  (category) => !!category && category.items.length > 0,
);

export const selectDeliveryMenuItems = createSelector(
  selectCombinedDeliveryMenu,
  reduce((all, category) => [...all, ...category.items], [] as MenuItem[]),
);

export const selectPickupMenuItems = createSelector(
  selectCombinedPickupMenu,
  reduce((all, category) => [...all, ...category.items], [] as MenuItem[]),
);

export const selectIndexedDeliveryMenuItems = createSelector(selectDeliveryMenuItems, (menuItems) => {
  return indexedMenuItems(menuItems);
});

export const selectIndexedPickupMenuItems = createSelector(selectPickupMenuItems, (menuItems) => {
  return indexedMenuItems(menuItems);
});

export const selectIndexedMenuItems = createSelector(
  selectIndexedDeliveryMenuItems,
  selectIndexedPickupMenuItems,
  selectDestination,
  (deliveryMenuItems, pickupMenuItems, destination) => {
    return destination === ooeConstants.DELIVERY ? deliveryMenuItems : pickupMenuItems;
  },
);
